// Cosmos Defence — single source of truth for alliance partner data
// Categories aligned to the new Strategic Technology Alliance Ecosystem
// Used by /alliances (overview) and /alliances/:slug (detail pages)

export const FILTER_CATEGORIES = [
  "All",
  "Endpoint Security",
  "Network Security",
  "Data Protection",
  "Zero Trust",
  "IAM / PAM",
  "SOC & SIEM",
  "Collaboration Security",
  "Cloud Security",
  "Governance & Compliance",
  "Document Security",
];

export const FEATURED_SLUGS = ["bitdefender", "norton", "cisco", "jabra"];

export const ALLIANCE_NETWORK = [
  {
    id: "technology-alliances",
    icon: "Network",
    title: "Technology Alliances",
    role: "Best-in-class security products integrated into customer outcomes",
    desc: "Cosmos Defence partners with global cybersecurity product leaders to deliver enterprise-ready platforms — pre-validated, deployment-tested and supported end-to-end by our team.",
    partners: ["bitdefender", "norton", "cisco", "jabra", "safetica", "secupi", "gtb-technologies", "axidian", "zaperon", "foxit", "abbyy"],
    accent: "from-[#3B82F6] to-[#6D28D9]",
  },
  {
    id: "enterprise-security-partners",
    icon: "Shield",
    title: "Enterprise Security Partners",
    role: "Vendors aligned to regulated and high-growth Indian enterprises",
    desc: "A focused subset of our alliances calibrated for BFSI, healthcare, manufacturing and government — chosen for compliance fit, deployment maturity and India-grade support.",
    partners: ["bitdefender", "norton", "cisco", "safetica", "axidian", "zaperon"],
    accent: "from-cyan-500 to-blue-500",
  },
  {
    id: "awareness-partners",
    icon: "Users",
    title: "Awareness Partners",
    role: "Platforms and collaborators powering the awareness movement",
    desc: "Cosmos Defence builds awareness in partnership with content platforms, training providers and community organisations — turning citizen safety into a scalable program.",
    partners: [],
    extras: ["RWAs & Senior Citizen Groups", "School & College Networks", "Merchant Associations", "1930 Helpline Partners"],
    accent: "from-emerald-500 to-teal-500",
  },
  {
    id: "workforce-partners",
    icon: "GraduationCap",
    title: "Workforce & Training Partners",
    role: "Academic, certification and skilling ecosystem collaborators",
    desc: "We work with universities, certification bodies and skilling initiatives to feed a continuous cyber talent pipeline — Skill India aligned and industry-ready.",
    partners: [],
    extras: ["Skill India Aligned Curriculum", "Industry-Academia Collaborations", "Certification Partners", "Internship & Placement Networks"],
    accent: "from-amber-500 to-orange-500",
  },
  {
    id: "channel-partners",
    icon: "Handshake",
    title: "Channel · MSP · Integrator Partners",
    role: "Distribution, resale and managed-service partner ecosystem",
    desc: "Cosmos Defence works through a tight, qualified network of channel partners, MSPs and system integrators — extending reach without diluting delivery quality.",
    partners: [],
    extras: ["Authorised Channel Partners", "Managed Service Providers (MSPs)", "System Integrators (SIs)", "Regional Distribution Partners"],
    accent: "from-violet-500 to-fuchsia-500",
  },
  {
    id: "future-collaborations",
    icon: "FlaskConical",
    title: "Future Security Collaborations",
    role: "Research, AI security and long-horizon strategic partnerships",
    desc: "An open lane for AI labs, threat-intel providers, academic researchers and government-aligned initiatives to collaborate on the next generation of cybersecurity defence.",
    partners: [],
    extras: ["AI Security Research", "Threat Intelligence Collaboration", "Government & Public-Sector Initiatives", "Open Research Programs"],
    accent: "from-rose-500 to-pink-500",
  },
];

// Helper: build a default brand mark (initials in branded gradient) when no licensed logo file is available
const wordmark = (letters, gradient = "from-[#3B82F6] to-[#6D28D9]") => ({
  type: "wordmark",
  letters,
  gradient,
});

export const PARTNERS = [
  {
    slug: "bitdefender",
    name: "Bitdefender",
    category: "Endpoint Security",
    featured: true,
    logo: wordmark("Bd", "from-[#ED1C24] to-[#7A0F12]"),
    tagline: "Industry-leading EDR / XDR for endpoints, servers and cloud workloads.",
    what:
      "Bitdefender is a global cybersecurity leader providing best-in-class endpoint protection, EDR, XDR, server security and cloud workload protection. It is consistently rated at the top of independent detection efficacy tests (AV-Comparatives, MITRE ATT&CK evaluations) while maintaining a light agent footprint suitable for production environments.",
    benefits: [
      "Top-tier independent detection efficacy with low false-positives",
      "Light agent — safe to deploy on production servers and VDI",
      "Unified console across endpoints, servers and cloud workloads",
      "Strong ransomware rollback and anti-tampering controls",
      "Mature integration with SIEM, SOAR and managed SOCs",
    ],
    useCases: [
      "Hybrid endpoint protection across Windows, macOS and Linux",
      "Server and Kubernetes workload protection",
      "VDI and roaming workforce defense",
      "Managed Endpoint Detection & Response as a service",
    ],
    deploymentValue:
      "Quick to deploy, easy to operate. Existing AV can be replaced in weeks — not quarters — with measurable improvement in detection rates and a meaningful reduction in operational noise for security teams.",
    cosmosSupport: [
      "Architecture sizing and licensing optimisation",
      "Migration from legacy AV with zero downtime",
      "Policy and exclusion tuning for production",
      "24/7 managed detection and response (MDR)",
      "Quarterly business reviews and posture reports",
    ],
    networks: ["technology-alliances", "enterprise-security-partners"],
  },
  {
    slug: "norton",
    name: "Norton",
    category: "Endpoint Security",
    featured: true,
    logo: wordmark("N", "from-[#FFC20E] to-[#F58220]"),
    tagline: "Trusted endpoint, identity and consumer-grade security for the workforce.",
    what:
      "Norton is one of the world's most recognised consumer and small-business cybersecurity brands — delivering endpoint protection, identity safety, VPN, password management and online threat protection. Cosmos Defence positions Norton as a trusted layer for mid-market workforce protection and BYOD-heavy environments.",
    benefits: [
      "Globally recognised, trusted consumer-to-business brand",
      "Endpoint, identity, VPN and password management unified",
      "Strong fit for BYOD and remote workforce protection",
      "Privacy and identity-theft protection layer",
      "Simple licensing model — fast to roll out",
    ],
    useCases: [
      "Mid-market and SMB endpoint protection",
      "BYOD and remote workforce identity safety",
      "Executive and high-risk-user identity protection",
      "VPN and privacy layer for distributed teams",
    ],
    deploymentValue:
      "Adds enterprise-grade identity, VPN and endpoint protection in days — backed by one of the most trusted security brands in the world.",
    cosmosSupport: [
      "Licensing optimisation for mid-market and SMB",
      "Rollout planning across mixed device fleets",
      "Identity-protection program design",
      "Awareness training tie-ins for end-users",
    ],
    networks: ["technology-alliances", "enterprise-security-partners"],
  },
  {
    slug: "cisco",
    name: "Cisco",
    category: "Network Security",
    featured: true,
    logo: wordmark("Ci", "from-[#1BA0D7] to-[#005073]"),
    tagline: "The backbone of enterprise networking, security and infrastructure.",
    what:
      "Cisco is the global standard for enterprise networking and infrastructure — and a deep, integrated portfolio across firewalls, secure access, identity, network detection and zero-trust architectures. Cosmos Defence partners with Cisco to deliver mission-critical network and security infrastructure for large Indian enterprises.",
    benefits: [
      "Globally proven enterprise networking foundation",
      "Integrated security across network, identity and access",
      "Deep ecosystem of certifications and trained engineers",
      "Long-term roadmap stability for critical infrastructure",
      "Strong fit for BFSI, government and large enterprise",
    ],
    useCases: [
      "Enterprise network architecture and segmentation",
      "Next-generation firewall and SD-WAN",
      "Identity Services Engine (ISE) and zero-trust access",
      "Secure collaboration and unified communications infrastructure",
    ],
    deploymentValue:
      "When the cost of downtime is unacceptable, Cisco delivers the proven backbone — and Cosmos Defence delivers the deployment and operating expertise around it.",
    cosmosSupport: [
      "Architecture design and capacity planning",
      "Deployment, configuration and hardening",
      "Network segmentation and zero-trust integration",
      "Ongoing operations and managed network services",
    ],
    networks: ["technology-alliances", "enterprise-security-partners"],
  },
  {
    slug: "jabra",
    name: "Jabra",
    category: "Collaboration Security",
    featured: true,
    logo: wordmark("Ja", "from-[#0066B2] to-[#003B71]"),
    tagline: "Secure, enterprise-grade collaboration audio and video for the modern workforce.",
    what:
      "Jabra delivers premium audio and video collaboration devices engineered for enterprise security, manageability and reliability. Cosmos Defence partners with Jabra to extend collaboration security into the device layer — particularly important as hybrid work makes endpoint devices the new perimeter.",
    benefits: [
      "Enterprise-grade audio and video collaboration devices",
      "Centralised manageability across distributed fleets",
      "Strong privacy and audio-security controls",
      "Long device lifecycle and reliability for high-use environments",
      "Certified for major UC platforms (Teams, Zoom, Webex)",
    ],
    useCases: [
      "Executive collaboration and boardroom AV security",
      "Hybrid-work device fleet standardisation",
      "Contact-centre audio reliability and privacy",
      "Conference-room video collaboration",
    ],
    deploymentValue:
      "Standardising on Jabra brings collaboration devices under the same governance umbrella as identity, endpoint and network security — closing a gap most enterprises overlook.",
    cosmosSupport: [
      "Device fleet planning and rollout",
      "Manageability and policy configuration",
      "Integration with UC platforms and identity",
      "Lifecycle and replacement program design",
    ],
    networks: ["technology-alliances", "enterprise-security-partners"],
  },
  {
    slug: "safetica",
    name: "Safetica",
    category: "Data Protection",
    logo: wordmark("Sa", "from-[#1E88E5] to-[#0D47A1]"),
    tagline: "Contextual DLP across endpoints, email and cloud — with operator-friendly policies.",
    what:
      "Safetica delivers data-loss prevention that's actually operational — combining endpoint, email and cloud channel coverage with policy templates aligned to DPDP, ISO and GDPR. Strong insider-risk analytics surface risky behaviour before incidents materialise.",
    benefits: [
      "Pre-built DPDP and ISO 27001 policy templates",
      "Insider-risk analytics with behavioural scoring",
      "Coverage of endpoint, email and major cloud SaaS",
      "Lightweight agent — practical for mid-market deployments",
      "Strong forensic timeline for investigations",
    ],
    useCases: [
      "Sensitive-data protection in BFSI, healthcare, manufacturing",
      "DPDP-aligned data classification and handling",
      "Insider-risk monitoring for departing employees",
      "Cloud SaaS data-leak prevention (M365, Google Workspace)",
    ],
    deploymentValue:
      "Most customers see policy hits within the first 30 days of deployment. Safetica is designed to make DLP an ongoing program — not a shelfware project.",
    cosmosSupport: [
      "Data classification and policy design workshops",
      "Phased deployment with shadow → block transitions",
      "Custom policies for industry-specific data flows",
      "Managed monitoring and incident triage",
    ],
    networks: ["technology-alliances", "enterprise-security-partners"],
  },
  {
    slug: "secupi",
    name: "SecuPi",
    category: "Data Protection",
    logo: wordmark("Se", "from-[#0EA5E9] to-[#0369A1]"),
    tagline: "Data-centric security and privacy enforcement for enterprise applications — no source code changes.",
    what:
      "SecuPi enforces privacy and security controls directly on enterprise applications — masking, tokenisation, encryption and access controls — without modifying app source code. Ideal for legacy systems where adding controls deeper is impractical.",
    benefits: [
      "No-code application enforcement of privacy policies",
      "Real-time masking, tokenisation and encryption",
      "Audit-grade access logs for every data interaction",
      "Privacy-preserving analytics and BI",
      "Strong fit for DPDP and GDPR enforcement",
    ],
    useCases: [
      "Data masking in CRM, ERP and analytics platforms",
      "DPDP / GDPR consent and purpose enforcement",
      "Third-party access control on sensitive datasets",
      "Audit-ready logging for regulatory reviews",
    ],
    deploymentValue:
      "Adds enterprise-grade privacy controls to apps you can't easily modify — critical for legacy systems and packaged enterprise software.",
    cosmosSupport: [
      "Data flow mapping and privacy assessment",
      "Policy design and phased enforcement",
      "Integration with existing IAM and SIEM",
      "Audit support and evidence collection",
    ],
    networks: ["technology-alliances"],
  },
  {
    slug: "gtb-technologies",
    name: "GTB Technologies",
    category: "Data Protection",
    logo: wordmark("Gt", "from-[#10B981] to-[#065F46]"),
    tagline: "Deep content-aware DLP for highly regulated industries.",
    what:
      "GTB Technologies provides one of the most content-aware DLP engines on the market — capable of inspecting and blocking sensitive data in real time across email, web, endpoint and cloud channels. Built for industries where data breaches carry the highest regulatory cost.",
    benefits: [
      "Deep content-aware inspection (text, files, structured data)",
      "Real-time blocking with low false-positive rate",
      "Regulatory packs for DPDP, GDPR, HIPAA and PCI",
      "Forensic incident playback for investigations",
      "Coverage across email, web, endpoint and cloud",
    ],
    useCases: [
      "Regulated industries with strict data residency",
      "PCI-DSS and HIPAA compliance enforcement",
      "Manufacturing IP protection",
      "Government and defence data classification",
    ],
    deploymentValue:
      "When DLP is non-negotiable — financial services, healthcare, government — GTB delivers the deep inspection rigour those regulators expect.",
    cosmosSupport: [
      "Data discovery and classification design",
      "Regulatory pack tuning per industry",
      "Phased policy rollout and enforcement",
      "Incident response integration",
    ],
    networks: ["technology-alliances"],
  },
  {
    slug: "axidian",
    name: "Axidian",
    category: "IAM / PAM",
    logo: wordmark("Ax", "from-[#8B5CF6] to-[#5B21B6]"),
    tagline: "Privileged access, MFA and identity governance — unified.",
    what:
      "Axidian unifies privileged access management, multi-factor authentication and identity governance into one platform — making identity your strongest control plane. Strong fit for hybrid Active Directory environments common in Indian enterprises.",
    benefits: [
      "PAM, MFA and IGA in a single integrated platform",
      "Session video recording for privileged access",
      "Strong hybrid Active Directory support",
      "Granular policy controls and just-in-time access",
      "Audit-ready evidence collection",
    ],
    useCases: [
      "Privileged admin account vaulting and rotation",
      "MFA enforcement on critical applications",
      "Identity governance and access reviews",
      "Just-in-time admin elevation",
    ],
    deploymentValue:
      "Consolidates three categories into one tool — reducing license sprawl, training overhead and integration cost while strengthening identity controls.",
    cosmosSupport: [
      "Privileged account discovery and inventory",
      "MFA rollout planning by application criticality",
      "Access review workflow design",
      "Ongoing identity hygiene and tuning",
    ],
    networks: ["technology-alliances", "enterprise-security-partners"],
  },
  {
    slug: "zaperon",
    name: "Zaperon",
    category: "Zero Trust",
    logo: wordmark("Za", "from-[#06B6D4] to-[#0E7490]"),
    tagline: "Modern ZTNA — replacing legacy VPN for hybrid workforces.",
    what:
      "Zaperon delivers Zero Trust Network Access — identity-driven, device-aware, least-privilege access to private apps. No inbound firewall rules, no exposed VPN concentrators, no flat-network risk.",
    benefits: [
      "Zero inbound firewall exposure",
      "Identity + device posture binding for every session",
      "Faster than legacy VPN for end-users",
      "Least-privilege access to private apps",
      "Strong fit for contractor and partner access",
    ],
    useCases: [
      "Replacing legacy VPN at scale",
      "Hybrid workforce and contractor access",
      "Partner and third-party access governance",
      "Reducing lateral-movement risk on the corporate network",
    ],
    deploymentValue:
      "Zaperon typically replaces legacy VPN in weeks — with measurable improvements in user experience and a major reduction in exposed network surface.",
    cosmosSupport: [
      "Application discovery and access mapping",
      "Phased migration from VPN to ZTNA",
      "Device posture and conditional access design",
      "Ongoing policy and access review",
    ],
    networks: ["technology-alliances", "enterprise-security-partners"],
  },
  {
    slug: "foxit",
    name: "Foxit",
    category: "Document Security",
    logo: wordmark("Fo", "from-[#F97316] to-[#9A3412]"),
    tagline: "PDF security, redaction and digital signatures for the enterprise.",
    what:
      "Foxit provides enterprise-grade PDF tools — including security, redaction, DRM and digital signatures. A lighter, faster alternative to legacy PDF suites with strong enterprise support.",
    benefits: [
      "Strong redaction and DRM controls",
      "Digital signature and e-signature workflows",
      "Lighter and faster than legacy PDF suites",
      "Cost-effective enterprise licensing",
      "Cross-platform support (desktop, mobile, web)",
    ],
    useCases: [
      "Legal, finance and HR document workflows",
      "Confidential PDF redaction",
      "e-Signature and digital signing workflows",
      "Document DRM and access control",
    ],
    deploymentValue:
      "Modern, secure PDF workflows without the legacy cost — practical for enterprises rationalising productivity stack expense.",
    cosmosSupport: [
      "Document workflow assessment",
      "DRM and redaction policy design",
      "Digital signature workflow integration",
      "User training and rollout",
    ],
    networks: ["technology-alliances"],
  },
  {
    slug: "abbyy",
    name: "ABBYY",
    category: "Document Security",
    logo: wordmark("Ab", "from-[#EC4899] to-[#9D174D]"),
    tagline: "Enterprise OCR and intelligent document processing — secure by design.",
    what:
      "ABBYY is a global leader in OCR and intelligent document processing. Cosmos Defence deploys ABBYY for use cases where sensitive documents must be processed accurately and securely — KYC, invoices, contracts and structured data extraction.",
    benefits: [
      "Enterprise-grade OCR accuracy",
      "Privacy-aware document extraction",
      "Process automation hooks",
      "Multi-language and document-type coverage",
      "Strong audit and compliance support",
    ],
    useCases: [
      "KYC and onboarding document processing",
      "Invoice and contract automation",
      "Regulatory document classification",
      "Sensitive data extraction with audit logs",
    ],
    deploymentValue:
      "Turns piles of unstructured documents into structured, secure, auditable data flows — a major lift for digital operations.",
    cosmosSupport: [
      "Document flow analysis and design",
      "Privacy-aware extraction policy",
      "Integration with downstream systems",
      "Compliance and audit support",
    ],
    networks: ["technology-alliances"],
  },
];

export const getPartnerBySlug = (slug) => PARTNERS.find((p) => p.slug === slug);
export const getPartnersByIds = (ids) => ids.map((id) => getPartnerBySlug(id)).filter(Boolean);
